import {
  Avatar,
  Checkbox,
  Divider,
  Icons,
  Input,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useUsers } from '../../../../api/created/users.ts';
import { AssignedToFilter } from '../../../../api/assignedToFilter.ts';
import {
  patientAssignedEmployeeIdsAtom,
  patientAssignedToAtom,
} from '../../../../state/patientSearchAtoms.ts';
import { getAvatarColor, getInitials, getUserLabel } from '../../../../utils/user.ts';

export function PatientTableAssignedToFilter() {
  const [assignedTo, setAssignedTo] = useAtom(patientAssignedToAtom);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useAtom(patientAssignedEmployeeIdsAtom);
  const [isExpanded, setIsExpanded] = useState(true);
  const [search, setSearch] = useState('');

  const { data: users } = useUsers();

  const sortedUsers = useMemo(() => {
    if (!users) return [];
    return [...users].sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b)));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (!trimmed) return sortedUsers;
    return sortedUsers.filter((user) => getUserLabel(user).toLowerCase().includes(trimmed));
  }, [sortedUsers, search]);

  const setPreset = (preset: AssignedToFilter) => {
    setAssignedTo(preset);
    setSelectedEmployeeIds(new Set<string>());
  };

  const toggleUser = (userId: string) => {
    setSelectedEmployeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      if (next.size === 0) {
        setAssignedTo(AssignedToFilter.ALL);
      } else {
        setAssignedTo(AssignedToFilter.SPECIFIC);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allVisibleIds = filteredUsers.map((u) => u.id);
    const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedEmployeeIds.has(id));
    if (allSelected) {
      setSelectedEmployeeIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.delete(id));
        if (next.size === 0) setAssignedTo(AssignedToFilter.ALL);
        return next;
      });
    } else {
      setSelectedEmployeeIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.add(id));
        return next;
      });
      setAssignedTo(AssignedToFilter.SPECIFIC);
    }
  };

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedEmployeeIds.has(u.id));

  return (
    <Stack gap={1} sx={{ px: 1, py: 0.5, minWidth: 280 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setIsExpanded((prev) => !prev)}>
        <Typography level="title-md">Mitarbeiter</Typography>
        {isExpanded ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
      </Stack>

      {isExpanded && (
        <Stack gap={1} sx={{ pt: 0.5 }}>
          <Input
            size="sm"
            placeholder="Mitarbeiter suchen"
            startDecorator={<Icons.Search />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Stack gap={0.5}>
            <Checkbox
              size="sm"
              label="Mir zugewiesen"
              checked={assignedTo === AssignedToFilter.ME}
              onChange={() => setPreset(AssignedToFilter.ME)}
            />
            <Checkbox
              size="sm"
              label="Alle Mitarbeiter"
              checked={assignedTo === AssignedToFilter.ALL}
              onChange={() => setPreset(AssignedToFilter.ALL)}
            />
            <Checkbox
              size="sm"
              label="Nicht zugewiesen"
              checked={assignedTo === AssignedToFilter.UNASSIGNED}
              onChange={() => setPreset(AssignedToFilter.UNASSIGNED)}
            />
          </Stack>

          <Divider />

          {filteredUsers.length > 0 && (
            <Checkbox
              size="sm"
              label="Alle auswählen"
              checked={allFilteredSelected}
              indeterminate={!allFilteredSelected && filteredUsers.some((u) => selectedEmployeeIds.has(u.id))}
              onChange={toggleSelectAll}
            />
          )}

          <Stack gap={0.5} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
            {filteredUsers.map((user) => (
              <Stack
                key={user.id}
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ cursor: 'pointer', py: 0.25 }}
                onClick={() => toggleUser(user.id)}>
                <Checkbox
                  size="sm"
                  checked={selectedEmployeeIds.has(user.id)}
                  onChange={() => toggleUser(user.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Avatar size="sm" color={getAvatarColor(user.id)}>
                  {getInitials(user)}
                </Avatar>
                <Typography level="body-sm" sx={{ flex: 1 }}>
                  {getUserLabel(user)}
                </Typography>
              </Stack>
            ))}
            {filteredUsers.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 1 }}>
                Keine Mitarbeiter gefunden
              </Typography>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
